import { deriveFieldFacts, IrdApiCapabilities } from '../field-decorators';
import { IrdEntitySchema } from '../metadata';
import { IrdProperty, IrdPropertyType } from '../property';
import { IrdMetadataLayer } from './layer';
import { IrdPropertyBuilder, IrdPropertyDeclaration } from './property-builder';
import { IrdSchemaFact } from './resolved';

/**
 * The entity level of a chain: the common ancestor every context level sits on.
 *
 * It is derived from the hand-written declaration an entity already has. There is deliberately no
 * separate schema layer in the interim — one declaration per entity carries both the schema facts
 * and the presentation, and the generated layer slots in beneath it later without any layer above
 * having to change.
 */
export class IrdEntityMetadataBuilder<T> extends IrdMetadataLayer<T> {
  protected readonly layerName: string;
  private facts: readonly IrdSchemaFact[];

  private constructor(metadata: IrdEntitySchema<T>, layerName: string) {
    super();
    this.layerName = layerName;
    const properties = Object.values(metadata.properties) as IrdProperty<T, IrdPropertyType>[];
    this.facts = properties.map((property) => ({
      key: property.nameAsString(),
      type: property.type,
      array: property.array,
      translationKey: property.getTranslationKey(),
      daoName: property.daoName,
      validators: property.validator ?? [],
      allowedValues: property.allowedValues,
      editable: property.editable,
    }));
    this.legacyHiddenKeys = new Set(properties.filter((property) => !property.showInUi).map((property) => property.nameAsString()));
    this.ownTranslationKeyPrefix = metadata.translationKeyPrefix;
    this.ownPrimaryKey = metadata.primaryKey as string & keyof T;
  }

  /**
   * Starts a chain from an entity's existing declaration.
   *
   * @param layerName how this layer is named in `IrdResolvedMetadata.explain()` output.
   */
  public static fromSchema<T>(metadata: IrdEntitySchema<T>, layerName = 'entity'): IrdEntityMetadataBuilder<T> {
    return new IrdEntityMetadataBuilder<T>(metadata, layerName);
  }

  /** As on every layer; the builder is named for this layer, whose name is not fixed in its type. */
  public override property<K extends string & keyof T>(key: K, declare: IrdPropertyDeclaration<IrdPropertyBuilder<T[K], 'entity'>>): this {
    return this.declareProperty(key, declare, new IrdPropertyBuilder<T[K], 'entity'>());
  }

  /**
   * Takes over what the API permits per field from the entity's decorated class — its
   * `@ApiCapabilities`, which the generator emits from the specification. Capability is a schema
   * fact (ADR-0003), so this is the only place it enters a chain; no view can declare it. A field
   * the decorated class names but this declaration does not is ignored.
   */
  public apiCapabilities(decorated: new (...args: never[]) => Partial<T>): this {
    const byKey = new Map<string, IrdApiCapabilities | undefined>(
      deriveFieldFacts(decorated).map((field) => [field.name, field.capabilities]),
    );
    this.facts = this.facts.map((fact) => ({ ...fact, capabilities: byKey.get(fact.key) }));
    return this;
  }

  protected override schemaFacts(): readonly IrdSchemaFact[] {
    return this.facts;
  }
}
